/**
 * ParameterDefinitionEditor Component
 * 
 * Editable component for a single parameter definition.
 * Supports both Prime (type definition only) and Company (actual values) modes.
 */

import React from 'react';
import { Input, InputNumber, Select, Switch, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { ParameterDefinition } from '../../types/transitionParameters';
import './ParameterDefinitionEditor.css';

const { Option } = Select;
const { TextArea } = Input;

interface ParameterDefinitionEditorProps {
  label: string;
  parameter: ParameterDefinition | undefined;
  onChange: (parameter: ParameterDefinition) => void;
  mode?: 'prime' | 'company';  // Prime = define types, Company = set values
  disabled?: boolean;
  tooltip?: string;
}

export const ParameterDefinitionEditor: React.FC<ParameterDefinitionEditorProps> = ({
  label,
  parameter,
  onChange,
  mode = 'company',
  disabled = false,
  tooltip,
}) => {
  const isPrime = mode === 'prime';
  
  // Initialize with defaults if undefined
  const param: ParameterDefinition = parameter || {
    type: 'number',
    value: null,
  };

  const handleChange = (field: keyof ParameterDefinition, value: any) => {
    onChange({
      ...param,
      [field]: value,
    });
  };

  const renderValueEditor = () => {
    if (isPrime) {
      // Prime mode: Show that value will be set in company
      return (
        <div className="parameter-value-placeholder">
          <Input 
            disabled 
            placeholder="Value set in company transition" 
            value="" 
          />
        </div>
      );
    }

    // Company mode: Edit actual value based on type
    switch (param.type) {
      case 'number':
        return (
          <InputNumber
            value={param.value as number}
            onChange={(value) => handleChange('value', value)}
            disabled={disabled}
            placeholder={param.default !== undefined ? `Default: ${param.default}` : 'Enter value'}
            style={{ width: '100%' }}
          />
        );
      
      case 'boolean':
        return (
          <Switch
            checked={param.value as boolean}
            onChange={(checked) => handleChange('value', checked)}
            disabled={disabled}
          />
        );
      
      case 'string':
        return (
          <Input
            value={param.value as string}
            onChange={(e) => handleChange('value', e.target.value)}
            disabled={disabled}
            placeholder={param.default !== undefined ? `Default: ${param.default}` : 'Enter value'}
          />
        );
      
      case 'array':
        return (
          <Select
            mode="tags"
            value={param.value as string[]}
            onChange={(value) => handleChange('value', value)}
            disabled={disabled}
            placeholder="Enter values"
            style={{ width: '100%' }}
          />
        );
      
      default:
        return (
          <TextArea
            value={typeof param.value === 'object' ? JSON.stringify(param.value, null, 2) : param.value}
            onChange={(e) => {
              try {
                const parsed = JSON.parse(e.target.value);
                handleChange('value', parsed);
              } catch {
                handleChange('value', e.target.value);
              }
            }}
            disabled={disabled}
            rows={3}
            placeholder="Enter value"
          />
        );
    }
  };

  return (
    <div className="parameter-definition-editor">
      <div className="parameter-header">
        <label className="parameter-label">
          {label}
          {tooltip && (
            <Tooltip title={tooltip}>
              <InfoCircleOutlined style={{ marginLeft: 8, color: '#1890ff' }} />
            </Tooltip>
          )}
        </label>
      </div>

      <div className="parameter-fields">
        {/* Type Selection (Prime mode only) */}
        {isPrime && (
          <div className="parameter-field">
            <label>Type:</label>
            <Select
              value={param.type}
              onChange={(value) => handleChange('type', value)}
              disabled={disabled}
              style={{ width: '100%' }}
            >
              <Option value="number">Number</Option>
              <Option value="boolean">Boolean</Option>
              <Option value="string">String</Option>
              <Option value="array">Array</Option>
              <Option value="object">Object</Option>
            </Select>
          </div>
        )}

        {/* Unit Selection (for number types) */}
        {param.type === 'number' && (
          <div className="parameter-field">
            <label>Unit:</label>
            <Input
              value={param.unit}
              onChange={(e) => handleChange('unit', e.target.value)}
              disabled={disabled || !isPrime}
              placeholder="e.g., units, kg, meters"
            />
          </div>
        )}

        {/* Time Period Selection (for rate-based numbers) */}
        {param.type === 'number' && (
          <div className="parameter-field">
            <label>Time Period:</label>
            <Select
              value={param.time_period}
              onChange={(value) => handleChange('time_period', value)}
              disabled={disabled || !isPrime}
              placeholder="Select time period"
              allowClear
              style={{ width: '100%' }}
            >
              <Option value="second">Second</Option>
              <Option value="minute">Minute</Option>
              <Option value="hour">Hour</Option>
              <Option value="day">Day</Option>
              <Option value="week">Week</Option>
              <Option value="month">Month</Option>
              <Option value="year">Year</Option>
            </Select>
          </div>
        )}

        {/* Currency Selection (for cost parameters) */}
        {param.type === 'number' && (
          <div className="parameter-field">
            <label>Currency:</label>
            <Select
              value={param.currency}
              onChange={(value) => handleChange('currency', value)}
              disabled={disabled || !isPrime}
              placeholder="Select currency"
              allowClear
              style={{ width: '100%' }}
            >
              <Option value="USD">USD</Option>
              <Option value="EUR">EUR</Option>
              <Option value="GBP">GBP</Option>
            </Select>
          </div>
        )}

        {/* Description */}
        <div className="parameter-field">
          <label>Description:</label>
          <Input
            value={param.description}
            onChange={(e) => handleChange('description', e.target.value)}
            disabled={disabled || !isPrime}
            placeholder="Describe this parameter"
          />
        </div>

        {/* Default Value (Prime mode) */}
        {isPrime && (
          <div className="parameter-field">
            <label>Default Value:</label>
            {param.type === 'number' ? (
              <InputNumber
                value={param.default as number}
                onChange={(value) => handleChange('default', value)}
                disabled={disabled}
                placeholder="Optional default"
                style={{ width: '100%' }}
              />
            ) : param.type === 'boolean' ? (
              <Switch
                checked={param.default as boolean}
                onChange={(checked) => handleChange('default', checked)}
                disabled={disabled}
              />
            ) : (
              <Input
                value={param.default as string}
                onChange={(e) => handleChange('default', e.target.value)}
                disabled={disabled}
                placeholder="Optional default"
              />
            )}
          </div>
        )}

        {/* Actual Value (Company mode) */}
        <div className="parameter-field">
          <label>
            Value:
            {!isPrime && param.default !== undefined && (
              <span className="parameter-default-hint"> (Default: {String(param.default)})</span>
            )}
          </label>
          {renderValueEditor()}
        </div>
      </div>
    </div>
  );
};

